import trace from '~/lib/trace';
import React, {useEffect, useState} from 'react';
import {View, ScrollView, ActivityIndicator} from 'react-native';
import {Divider} from 'react-native-paper';

import sy from '~/styles';
import {
  Text,
  RadioButton,
  ActionButton,
  Pressable,
  Dialog,
  FormTextInput,
  Link,
} from '~/components/controls';
import Actionsheet, {
  Title,
  Content,
  Actions,
} from '~/components/controls/Actionsheet';
import withDimensions from '~/components/hoc/with-dimensions';
import {dealer as dealerApi} from '~/api/private';
import {address as addressApi} from '~/api/public';

import AddIcon from '~/images/md-icons/add/materialicons/24px.svg';
import AccountCircleIcon from '~/images/md-icons/account_circle/materialicons/24px.svg';
import PhoneIcon from '~/images/md-icons/phone/materialicons/24px.svg';
import HomeFilledIcon from '~/images/md-icons/home_filled/materialicons/24px.svg';
import DeleteIcon from '~/images/md-icons/delete/materialicons/24px.svg';
import PropTypes from 'prop-types';

const HEADER_HEIGHT_IN_PX = 72;
const FOOTER_HEIGHT_IN_PX = 50;
const DIVIDER_HEIGHT_IN_PX = 1;

const DeliveryLocationActionsheet = ({
  dealer_id,
  location,
  dimensions,
  onDismiss,
  onChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(location?.id ?? null);
  const [locations, setLocations] = useState([]);
  const [addEditLocation, setAddEditLocation] = useState(null);

  const refresh = async () => {
    const {result} = await dealerApi.delivery_locations(dealer_id);
    setLocations(result);
    setLoading(false);
  };

  useEffect(() => {
    refresh().catch(trace);
  }, [dealer_id]);

  useEffect(() => {
    if (addEditLocation?.postal_code && addEditLocation?.house_number) {
      const fetch = async () => {
        const result = await addressApi.fetch(
          addEditLocation.postal_code,
          addEditLocation.house_number,
        );

        if (result) {
          setAddEditLocation({
            ...addEditLocation,
            ...result,
          });
        }
      };

      fetch().catch(trace);
    }
  }, [addEditLocation?.postal_code, addEditLocation?.house_number]);

  const onAddEditLocation = async () => {
    if (
      !addEditLocation?.name ||
      !addEditLocation?.phone_number ||
      !addEditLocation?.postal_code ||
      !addEditLocation?.house_number ||
      !addEditLocation?.street ||
      !addEditLocation?.city
    ) {
      return;
    }

    if (addEditLocation?.id) {
      const {success} = await dealerApi.update_delivery_location(
        dealer_id,
        addEditLocation.id,
        {
          ...addEditLocation,
        },
      );

      if (success) {
        setAddEditLocation(null);
      }
    } else {
      const {success, location_id} = await dealerApi.add_delivery_location(
        dealer_id,
        {
          ...addEditLocation,
        },
      );

      if (success) {
        refresh().catch(trace);
        setAddEditLocation(null);
        setSelected(location_id);
      }
    }
  };

  const onDeleteLocation = async (location) => {
    if (addEditLocation?.id) {
      const {success} = await dealerApi.delete_delivery_location(
        dealer_id,
        addEditLocation.id,
      );

      if (success) {
        refresh().catch(trace);
        setAddEditLocation(null);
        setSelected(null);
      }
    }
  };

  const onOK = () => {
    onChange(locations.find((item) => item.id === selected));
  };

  return (
    <>
      <Actionsheet visible={true} onDismiss={onDismiss}>
        {loading && (
          <View style={[sy['p-4']]}>
            <ActivityIndicator size="large" color="#231fda" />
          </View>
        )}
        {!loading && (
          <>
            <Title>Locatie</Title>
            <Divider style={sy['bg-gray88']} />
            <Content style={{paddingBottom: 0, paddingHorizontal: 0}}>
              <ScrollView
                contentContainerStyle={{
                  maxHeight:
                    dimensions.window.height -
                    HEADER_HEIGHT_IN_PX -
                    DIVIDER_HEIGHT_IN_PX -
                    FOOTER_HEIGHT_IN_PX -
                    DIVIDER_HEIGHT_IN_PX,
                }}>
                <View style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
                  <Pressable
                    style={[sy['p-4'], sy['pr-0']]}
                    onPress={() => {
                      setSelected(null);
                    }}>
                    <RadioButton checked={selected === null} />
                  </Pressable>
                  <View style={[sy['flex-1']]}>
                    <View style={[sy['py-4'], sy['pr-4']]}>
                      <Text>Eigen locatie</Text>
                    </View>
                    <Divider style={[sy['bg-gray95']]} />
                  </View>
                </View>
                {locations.map((location) => (
                  <View
                    key={location.id}
                    style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
                    <Pressable
                      style={[sy['p-4'], sy['pr-0']]}
                      onPress={() => {
                        setSelected(location.id);
                      }}>
                      <RadioButton checked={selected === location.id} />
                    </Pressable>
                    <View style={[sy['flex-1']]}>
                      <View style={[sy['py-4'], sy['pr-4']]}>
                        <Text>{location.name}</Text>
                        <Link
                          textStyle={[sy.small, sy['text-lightgray']]}
                          onPress={() => setAddEditLocation(location)}>
                          {location.street}&nbsp;{location.house_number},&nbsp;
                          {location.city}
                        </Link>
                      </View>
                      <Divider style={[sy['bg-gray95']]} />
                    </View>
                  </View>
                ))}
                <Pressable
                  style={[sy['flex-row'], sy['p-4'], sy['items-center']]}
                  onPress={() => {
                    setAddEditLocation({});
                  }}>
                  <AddIcon fill="#2907E3" />
                  <Text style={[sy['pl-4']]}>Locatie toevoegen</Text>
                </Pressable>
              </ScrollView>
              <Divider style={sy['bg-gray88']} />
            </Content>
            <Actions>
              <ActionButton onPress={onDismiss}>Annuleren</ActionButton>
              <ActionButton onPress={onOK}>OK</ActionButton>
            </Actions>
          </>
        )}
      </Actionsheet>
      <Dialog
        visible={!!addEditLocation}
        title={
          addEditLocation?.id ? (
            <View
              style={[
                sy['flex-row'],
                sy['justify-between'],
                sy['items-center'],
                {
                  display: 'flex',
                },
              ]}>
              <Text style={[sy.large]}>Locatie bewerken</Text>
            </View>
          ) : (
            <Text style={[sy.large]}>Locatie toevoegen</Text>
          )
        }
        titleIcon={
          addEditLocation?.id ? (
            <Pressable onPress={() => onDeleteLocation()}>
              <DeleteIcon fill="#4A4A49" />
            </Pressable>
          ) : null
        }
        onDismiss={() => setAddEditLocation(null)}
        options={{noPaddingContent: true}}
        buttons={[
          {
            text: 'Annuleren',
            onPress: () => setAddEditLocation(null),
          },
          {
            text: 'OK',
            disabled:
              !addEditLocation?.name ||
              !addEditLocation?.phone_number ||
              !addEditLocation?.postal_code ||
              !addEditLocation?.house_number ||
              !addEditLocation?.street ||
              !addEditLocation?.city,
            onPress: onAddEditLocation,
          },
        ]}>
        <View
          style={[
            sy['p-4'],
            sy['pb-0'],
            sy['flex-row'],
            sy['items-center'],
            sy['gap-3'],
          ]}>
          <AccountCircleIcon fill="4a4a49" />
          <FormTextInput
            style={[sy['flex-1']]}
            label="Bedrijfsnaam en/of contactpersoon"
            value={addEditLocation?.name}
            onChangeText={(name) =>
              setAddEditLocation((prevState) =>
                prevState ? {...prevState, name} : {name},
              )
            }
          />
        </View>
        <View
          style={[
            sy['p-4'],
            sy['pb-0'],
            sy['flex-row'],
            sy['items-center'],
            sy['gap-3'],
          ]}>
          <PhoneIcon fill="4a4a49" />
          <FormTextInput
            style={[sy['flex-1']]}
            label="Telefoonnummer"
            value={addEditLocation?.phone_number}
            onChangeText={(phone_number) =>
              setAddEditLocation((prevState) =>
                prevState ? {...prevState, phone_number} : {phone_number},
              )
            }
          />
        </View>
        <View
          style={[
            sy['p-4'],
            sy['pb-0'],
            sy['flex-row'],
            sy['items-center'],
            sy['gap-3'],
          ]}>
          <HomeFilledIcon fill="4a4a49" />
          <View style={[sy['flex-row'], sy['flex-1'], sy['gap-4']]}>
            <FormTextInput
              style={[sy['flex-1']]}
              label="Postcode"
              value={addEditLocation?.postal_code}
              onChangeText={(value) => {
                const postal_code = value
                  .replace(/[^0-9a-z]/gi, '')
                  .toUpperCase()
                  .trim();
                setAddEditLocation((prevState) =>
                  prevState ? {...prevState, postal_code} : {postal_code},
                );

                return postal_code;
              }}
              inputProps={{
                maxLength: 6,
              }}
            />
            <FormTextInput
              style={[sy['flex-1']]}
              label="Huisnummer"
              value={addEditLocation?.house_number}
              onChangeText={(house_number) =>
                setAddEditLocation((prevState) =>
                  prevState ? {...prevState, house_number} : {house_number},
                )
              }
            />
          </View>
        </View>
        <View
          style={[
            sy['p-4'],
            sy['pb-0'],
            sy['pl-13'],
            sy['flex-row'],
            sy['items-center'],
            sy['gap-3'],
          ]}>
          <FormTextInput
            label="Straat"
            style={[sy['flex-1']]}
            value={addEditLocation?.street}
            onChangeText={(street) =>
              setAddEditLocation((prevState) =>
                prevState ? {...prevState, street} : {street},
              )
            }
          />
        </View>
        <View
          style={[
            sy['p-4'],
            sy['pl-13'],
            sy['flex-row'],
            sy['items-center'],
            sy['gap-3'],
          ]}>
          <FormTextInput
            label="Plaats"
            style={[sy['flex-1']]}
            value={addEditLocation?.city}
            onChangeText={(city) =>
              setAddEditLocation((prevState) =>
                prevState ? {...prevState, city} : {city},
              )
            }
          />
        </View>
      </Dialog>
    </>
  );
};
DeliveryLocationActionsheet.propTypes = {
  dealer_id: PropTypes.any,
  location: PropTypes.any,
  dimensions: PropTypes.any,
  onDismiss: PropTypes.any,
  onChange: PropTypes.any,
};

export default withDimensions(DeliveryLocationActionsheet);
